import type { RouteObject } from 'react-router';

import { NotFoundPage } from '@pages/not-found';
import { APP_PATH } from '@shared/config';
import { ROLE, type Role } from '@shared/session';
import { RouteErrorBoundary } from '@shared/ui/error-boundary';

import { AppFormLayout } from '../layouts/app-form.layout';
import { AppStackLayout } from '../layouts/app-stack.layout';
import { GuestCenterLayout } from '../layouts/guest-center.layout';
import type { ScreenHandle, ScreenInfo } from '../layouts/screen-handle';
import { routeMeta } from './route-meta';

/**
 * 실제 서비스 라우트 트리(DD-18). 경로는 기존 서비스(`~/dev/d3/d3-repo` `routes/*.route.tsx`)와 같고
 * 권한은 DD-14 Q9 를 따른다.
 * 표: `work/project/d3-dashboard/design-preview/screens-app.md`.
 *
 * 붙이는 법
 * 1. 로그인 없이 보는 화면은 `servicePublicRoutes`(T3 `GuestCenterLayout` 아래).
 *    토큰이 든 공개 링크는 들어올 때 기존 세션을 정리한다(`resetSession`) — S03 을 실제로 붙일 때.
 * 2. 역할마다 화면이 다르므로 **역할별 트리**. 그 역할이 아니면 트리가 마운트되지 않는다.
 *    여러 역할이 함께 쓰는 화면은 아래 `SCREEN` 의 라우트를 역할마다 펼쳐 넣는다.
 * 3. 화면은 템플릿 레이아웃(T1 `AppStackLayout` · T2 `AppFormLayout`) 아래에 둔다. 레이아웃이 머리
 *    (배너 · 헤더 · BackBar)와 본문 폭을 그리고, 화면(page)은 본문만 그린다.
 * 4. 페이지는 `lazy` 로 직접 import 한다(배럴 금지 — 코드 스플리팅).
 */

type Lazy = NonNullable<RouteObject['lazy']>;

function screen(path: string, lazy: Lazy, info: ScreenInfo, publicRoute = false): RouteObject {
  const handle: ScreenHandle & Partial<ReturnType<typeof routeMeta>> = {
    screen: info,
    ...(publicRoute ? routeMeta({ auth: 'public' }) : {}),
  };
  return { path, lazy, ErrorBoundary: RouteErrorBoundary, handle };
}

const page = {
  login: async () => ({ Component: (await import('@pages/login')).LoginPage }),
  findPassword: async () => ({
    Component: (await import('@pages/find-password')).FindPasswordPage,
  }),
  resetPassword: async () => ({
    Component: (await import('@pages/reset-password')).ResetPasswordPage,
  }),
  main: async () => ({ Component: (await import('@pages/main')).MainPage }),
  fundDetail: async () => ({ Component: (await import('@pages/fund-detail')).FundDetailPage }),
  fundEdit: async () => ({ Component: (await import('@pages/fund-edit')).FundEditPage }),
  portfolioDetail: async () => ({
    Component: (await import('@pages/portfolio-detail')).PortfolioDetailPage,
  }),
  portfolioCreate: async () => ({
    Component: (await import('@pages/portfolio-create')).PortfolioCreatePage,
  }),
  portfolioEdit: async () => ({
    Component: (await import('@pages/portfolio-edit')).PortfolioEditPage,
  }),
  portfolioAddData: async () => ({
    Component: (await import('@pages/portfolio-add-data')).PortfolioAddDataPage,
  }),
  portfolioCaptable: async () => ({
    Component: (await import('@pages/portfolio-captable')).PortfolioCaptablePage,
  }),
  portfolioReport: async () => ({
    Component: (await import('@pages/portfolio-report')).PortfolioReportPage,
  }),
  portfolioEvaluation: async () => ({
    Component: (await import('@pages/portfolio-evaluation')).PortfolioEvaluationPage,
  }),
  fileManage: async () => ({ Component: (await import('@pages/file-manage')).FileManagePage }),
  memberManage: async () => ({
    Component: (await import('@pages/member-manage')).MemberManagePage,
  }),
  mailSchedule: async () => ({
    Component: (await import('@pages/mail-schedule')).MailSchedulePage,
  }),
  accountInfo: async () => ({ Component: (await import('@pages/account-info')).AccountInfoPage }),
  exchangeRate: async () => ({
    Component: (await import('@pages/exchange-rate')).ExchangeRatePage,
  }),
  accessLog: async () => ({ Component: (await import('@pages/access-log')).AccessLogPage }),
} satisfies Record<string, Lazy>;

/** 화면 라우트. 역할 트리에 골라 넣는다 */
const SCREEN = {
  // T3 Guest / Center
  S01: screen(APP_PATH.login, page.login, { id: 'S01', card: 'login' }, true),
  S02: screen(APP_PATH.findPassword, page.findPassword, { id: 'S02', card: 'narrow' }, true),
  S03: screen(APP_PATH.resetPassword, page.resetPassword, { id: 'S03', card: 'narrow' }, true),
  // T1 App / Stack
  S04: screen(APP_PATH.root, page.main, { id: 'S04', backBar: false }),
  S05: screen(APP_PATH.fundDetail, page.fundDetail, { id: 'S05' }),
  S08: screen(APP_PATH.portfolioDetail, page.portfolioDetail, { id: 'S08' }),
  /** PF 의 포트폴리오 상세 — BackBar 없음(screens.md 1.1) */
  S08pf: screen(APP_PATH.portfolioDetail, page.portfolioDetail, { id: 'S08', backBar: false }),
  S11: screen(APP_PATH.portfolioAddData, page.portfolioAddData, { id: 'S11' }),
  S12: screen(APP_PATH.portfolioCaptable, page.portfolioCaptable, { id: 'S12' }),
  S13: screen(APP_PATH.portfolioReport, page.portfolioReport, { id: 'S13', menuRoot: true }),
  S14: screen(APP_PATH.portfolioEvaluation, page.portfolioEvaluation, {
    id: 'S14',
    menuRoot: true,
  }),
  S15: screen(APP_PATH.file, page.fileManage, { id: 'S15', menuRoot: true }),
  S16: screen(APP_PATH.member, page.memberManage, { id: 'S16', menuRoot: true }),
  // 헤더 메뉴가 아니라 S15 의 버튼으로 연다 — BackBar 는 "파일 관리 - 예약 메일 설정"(화면 트리)
  S17: screen(APP_PATH.mail, page.mailSchedule, { id: 'S17' }),
  S20: screen(APP_PATH.accessLog, page.accessLog, { id: 'S20', menuRoot: true }),
  // T2 App / Form
  S06: screen(APP_PATH.fundWrite, page.fundEdit, { id: 'S06' }),
  S07: screen(APP_PATH.fundEdit, page.fundEdit, { id: 'S07' }),
  S09: screen(APP_PATH.portfolioWrite, page.portfolioCreate, { id: 'S09' }),
  S10: screen(APP_PATH.portfolioEdit, page.portfolioEdit, { id: 'S10' }),
  S18: screen(APP_PATH.info, page.accountInfo, { id: 'S18', menuRoot: true }),
  S19: screen(APP_PATH.config, page.exchangeRate, { id: 'S19', menuRoot: true }),
} satisfies Record<string, RouteObject>;

/** 템플릿 레이아웃으로 화면 라우트를 묶는다 */
const stack = (...children: RouteObject[]): RouteObject => ({
  Component: AppStackLayout,
  children,
});
const form = (...children: RouteObject[]): RouteObject => ({ Component: AppFormLayout, children });

/**
 * S21 페이지 없음(T1, BackBar 없음 — DD-15 H19). 로그인한 사용자의 역할 트리에 없는 주소.
 * `create-routes` 가 가드 아래 맨 끝에 둔다. 게스트는 가드가 로그인으로 보낸다.
 */
export const serviceNotFoundRoute: RouteObject = stack({
  path: '*',
  Component: NotFoundPage,
  handle: { screen: { id: 'S21', backBar: false } } satisfies ScreenHandle,
});

export const servicePublicRoutes: RouteObject[] = [
  { Component: GuestCenterLayout, children: [SCREEN.S01, SCREEN.S02, SCREEN.S03] },
];

/** D3 · Admin 이 함께 쓰는 화면. 환율 정보 · 접속 로그는 Admin 만(DD-14 Q9 a) */
const staffStack = [
  SCREEN.S04,
  SCREEN.S05,
  SCREEN.S08,
  SCREEN.S11,
  SCREEN.S12,
  SCREEN.S13,
  SCREEN.S14,
  SCREEN.S15,
  SCREEN.S16,
  SCREEN.S17,
];
const staffForm = [SCREEN.S06, SCREEN.S07, SCREEN.S09, SCREEN.S10, SCREEN.S18];

export const serviceRoleRoutes: Record<Role, RouteObject[]> = {
  [ROLE.D3]: [stack(...staffStack), form(...staffForm)],
  [ROLE.ADMIN]: [stack(...staffStack, SCREEN.S20), form(...staffForm, SCREEN.S19)],
  // LP 는 포트폴리오 평가를 쓰지 않는다(DD-14 Q9 e)
  [ROLE.LP]: [stack(SCREEN.S04, SCREEN.S05, SCREEN.S08), form(SCREEN.S18)],
  // PF 의 `/` 는 메인 화면이 자기 첫 포트폴리오 상세로 보낸다
  [ROLE.PORTFOLIO]: [stack(SCREEN.S04, SCREEN.S08pf, SCREEN.S11), form(SCREEN.S10, SCREEN.S18)],
};
